import { useEffect, useState } from 'react';
import { Button, Field, Modal } from './kit';
import { ApiRequestError } from '../api/client';

type ResetResult = { generated: boolean; password?: string };

/** Bir kullanıcının (bayi/personel) şifresini sıfırlama modalı. Boş bırakılırsa sunucu üretir. */
export function ResetPasswordModal({
  target,
  resetFn,
  onClose,
}: {
  target: { id: number; label: string } | null;
  resetFn: (id: number, newPassword?: string) => Promise<ResetResult>;
  onClose: () => void;
}) {
  const [pwd, setPwd] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (target) {
      setPwd('');
      setError(null);
      setResult(null);
      setDone(false);
      setCopied(false);
    }
  }, [target]);

  const submit = async () => {
    if (!target) return;
    setError(null);
    setBusy(true);
    try {
      const r = await resetFn(target.id, pwd.trim() || undefined);
      setResult(r.password ?? null);
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'İşlem başarısız');
    } finally {
      setBusy(false);
    }
  };

  const copy = () => {
    if (!result) return;
    navigator.clipboard?.writeText(result).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  };

  return (
    <Modal
      open={target !== null}
      onClose={onClose}
      eyebrow="Şifre"
      title="Şifre Sıfırla"
      sub={target?.label}
      size="sm"
      footer={
        done ? (
          <Button onClick={onClose}>Kapat</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose}>Vazgeç</Button>
            <Button onClick={submit} disabled={busy}>{busy ? 'Sıfırlanıyor…' : 'Şifreyi Sıfırla'}</Button>
          </>
        )
      }
    >
      {error && <div className="auth-error" style={{ marginBottom: 12 }}>{error}</div>}

      {!done ? (
        <>
          <p style={{ color: 'var(--muted)', fontSize: 13.5, marginBottom: 16, lineHeight: 1.55 }}>
            Yeni şifreyi belirleyin. Boş bırakırsanız sistem güçlü bir şifre üretir ve
            ekranda gösterir. Mevcut şifre gerekmez.
          </p>
          <Field
            label="Yeni şifre (boş = otomatik üret)"
            id="reset-pw"
            type="text"
            placeholder="En az 8 karakter…"
            value={pwd}
            autoComplete="off"
            onChange={(e) => setPwd(e.target.value)}
          />
        </>
      ) : result ? (
        <>
          <p style={{ color: 'var(--muted)', fontSize: 13.5, marginBottom: 4, lineHeight: 1.55 }}>
            Yeni şifre oluşturuldu. Bu şifre yalnızca <strong>şimdi</strong> gösterilir — kopyalayıp iletin.
          </p>
          <div className="pw-result">
            <span>{result}</span>
            <Button size="sm" variant="secondary" onClick={copy}>{copied ? '✓ Kopyalandı' : 'Kopyala'}</Button>
          </div>
        </>
      ) : (
        <p style={{ color: 'var(--good)', fontSize: 14, fontWeight: 540 }}>
          ✓ Şifre güncellendi. Belirlediğiniz şifreyle giriş yapılabilir.
        </p>
      )}
    </Modal>
  );
}
